import type { Appearance } from "@stripe/stripe-js";
import type { StripeTheme } from "./useThemeContext";

const fontFamily = `system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`;

// Helper function to get computed CSS variable value
const getCSSVariable = (variableName: string): string => {
  if (typeof window !== "undefined") {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(variableName)
      .trim();
    return value || "";
  }
  return "";
};

export const createInAppAppearance = (stripeTheme: StripeTheme): Appearance => {
  // Get ALL styling from CSS variables to ensure perfect matching
  const textColor = getCSSVariable("--color-text-dark") || "#19283a";
  const backgroundColor =
    getCSSVariable("--color-background-primary") || "hsl(0, 0%, 100%)";
  const borderColor = getCSSVariable("--color-border-primary") || "#d2d7db";
  const focusBorderColor =
    getCSSVariable("--color-border-light") || "hsl(0, 0%, 87.1%)";
  const placeholderColor =
    getCSSVariable("--color-text-placeholder") || "rgb(162, 173, 190)";

  // We have to use Tailwind's `red-500` color to match form error styling because our Forms use
  // `@apply text-red-500` and `@apply border-red-500` in both light and dark mode instead of the
  // `color-feedback-error` CSS var.
  const errorColor = "#EF4444"; // Tailwind red-500

  // Get typography and layout from CSS variables too
  const fontFamilyFromCSS = getCSSVariable("--font-system-ui") || fontFamily;
  const borderRadiusFromCSS = "8px";

  return {
    theme: stripeTheme,
    disableAnimations: true,
    variables: {
      spacingGridRow: "0",
      borderRadius: borderRadiusFromCSS,
      fontFamily: fontFamilyFromCSS,
      fontSizeBase: "16px",
      fontWeightNormal: "400",
      fontWeightMedium: "500",
    },
    rules: {
      [".AccordionItem"]: {
        backgroundColor,
        boxShadow: "none",
      },
      [".Block"]: {
        backgroundColor,
      },
      [".Label"]: {
        color: textColor,
        fontFamily: fontFamilyFromCSS,
        fontSize: "15px",
        fontWeight: "600",
        lineHeight: "22.5px",
        margin: "19px 0 4px 0", // Using 19px to match `margin-top=-1px; padding-top=20px` in our `form-group` styles
        padding: "0 0 0 0",
      },
      [".Input"]: {
        backgroundColor,
        border: `1px solid ${borderColor}`,
        borderRadius: borderRadiusFromCSS,
        boxShadow: "none",
        color: textColor,
        fontFamily: fontFamilyFromCSS,
        fontSize: "16px",
        fontWeight: "400",
        lineHeight: "20px",
        margin: "0 0 8px 0",
        outline: "transparent solid 0px",
        outlineOffset: "0",
        padding: "12px 16px",
        transition: "ease 0.2s border",
        width: "100%",
      },
      [".Input:focus"]: {
        border: `1px solid ${focusBorderColor}`,
        boxShadow: "none",
        outline: "transparent solid 2px",
        outlineOffset: "0",
      },
      [".Input::placeholder"]: {
        color: placeholderColor,
        opacity: "0.5",
      },
      [".Input--invalid"]: {
        color: errorColor,
        borderColor: errorColor,
        boxShadow: "none",
      },
      [".Input--invalid:focus"]: {
        color: errorColor,
        borderColor: errorColor,
        boxShadow: "none",
      },
      [".Error"]: {
        color: errorColor,
        fontSize: "14px",
        lineHeight: "20px",
        marginBottom: "8px",
        marginTop: "0",
      },
      [".TermsText"]: {
        fontSize: "0",
      },
    },
  };
};
